// Display names for the event-summary buckets, keyed by the platform that wrote the run.
//
// The bucket KEYS are the contract (aeshared-js getEventSummaries, the cached
// event_summaries.json, the autoinspect prompt) and are identical on both platforms;
// only what the user sees changes. On macOS the same key carries the macOS twin —
// a "Registry" write is a config plist / receipt / launchd job write, a "Service" is a
// launchd daemon, a "Start Menu item" is an .app bundle in an Applications folder or a
// Dock icon (aebuild plans/macos-watch/summary-events.md).

const WINDOWS_LABELS = {
  InstallerInfo: 'Installer Info',
  RegistryTreesWritten: 'Registry Trees Written',
  RegistryASEPsWritten: 'Registry ASEPs Written',
  DirectoriesWritten: 'Directories Written',
  InterestingDirectoriesWritten: 'Interesting Directories Written',
  BitsFilesInfo: 'BITS Files',
  DnsLookups: 'DNS Lookups',
  TcpIpConnections: 'TCP/IP Connections',
  TaskLifeCycles: 'Scheduled Tasks',
  CertificateLifeCycles: 'Certificates',
  FirewallRuleLifeCycles: 'Firewall Rules',
  StartupLifeCycles: 'Startup Items',
  UninstallerLifeCycles: 'Uninstaller Entries',
  StartMenuItemLifeCycles: 'Start Menu Items',
  DesktopItemLifeCycles: 'Desktop Items',
  ServiceLifeCycles: 'Services',
  InterestingFileInfo: 'Interesting Files',
  ProcessLifeCycles: 'Processes',
};

const MACOS_LABELS = {
  ...WINDOWS_LABELS,
  RegistryTreesWritten: 'Config Domains Written',
  RegistryASEPsWritten: 'Interesting Config (persistence / hijack / security)',
  BitsFilesInfo: 'Downloads (quarantine)',
  TaskLifeCycles: 'launchd Timers / cron',
  CertificateLifeCycles: 'Certificates (keychain / trust settings)',
  FirewallRuleLifeCycles: 'Firewall (application firewall / pf)',
  StartupLifeCycles: 'Login & Launch Items',
  UninstallerLifeCycles: 'Package Receipts',
  StartMenuItemLifeCycles: 'Launch Points (Applications, Dock)',
  DesktopItemLifeCycles: 'Desktop Items',
  ServiceLifeCycles: 'launchd Daemons',
  InterestingFileInfo: 'Interesting Files (Mach-O / bundles)',
};

/** Human-readable OS names for the `Platform` tag ("windows" | "macos"). */
export const PLATFORM_LABELS = { windows: 'Windows', macos: 'macOS' };

export function isMacPlatform(platform) {
  return typeof platform === 'string' && platform.toLowerCase() === 'macos';
}

/**
 * Display name for a summary bucket key. Unknown keys come back unchanged so a new
 * bucket still renders something sensible.
 * @param {string} key       bucket key, e.g. 'ServiceLifeCycles'
 * @param {string} [platform] 'windows' | 'macos' (default windows)
 */
export function getBucketLabel(key, platform) {
  const table = isMacPlatform(platform) ? MACOS_LABELS : WINDOWS_LABELS;
  return table[key] || key;
}

/** The per-row "Bits Info:" block heading — BITS is a Windows service; on macOS it is the quarantine record. */
export function getDownloadInfoLabel(platform) {
  return isMacPlatform(platform) ? 'Download info:' : 'Bits Info:';
}

/** "Windows" / "macOS" for a Platform tag, with the PlatformVersion as detail when known. */
export function getPlatformLabel(platform) {
  if (!platform) return '';
  return PLATFORM_LABELS[String(platform).toLowerCase()] || platform;
}
